import { createReducer, on } from "@ngrx/store";
import { ToastMessage } from "../../models/common.model";

import * as AppActions from "./app.actions";
import { VigilantApiDtosUsersSelfGetResponse } from "../../api/vigilant/users/users.model";
export { AppActions };

/**
 * Represents the application state.
 *
 * @typedef {Object} AppState
 * @property {string | undefined} apiVersion - The current API version being used.
 * @property {string[]} breadcrumbs - An array of breadcrumb navigation items.
 * @property {boolean} dialogVisible - Indicates whether a dialog is visible.
 * @property {boolean} dialogModal - Indicates whether the dialog is modal.
 * @property {string | undefined} dialogTitle - The title of the dialog, if provided.
 * @property {string | undefined} dialogMessage - The message content of the dialog, if provided.
 * @property {boolean} overlayVisible - Indicates whether an overlay is visible.
 * @property {boolean} spinnerVisible - Indicates whether a loading spinner is visible.
 * @property {ToastMessage[]} toasts - An array of toast notifications.
 * @property {boolean} userLoggedIn - Indicates whether the user is logged in.
 * @property {any} userAuthData - Data related to user authentication.
 * @property {boolean | undefined} userSuperAdmin - Indicates whether the user has super admin privileges.
 * @property {number | undefined} userId - The user's unique identifier.
 * @property {string | undefined} userEmail - The user's email address.
 * @property {string | undefined} userFirstName - The user's first name.
 * @property {string | undefined} userLastName - The user's last name.
 * @property {number | null | undefined} userRootOrgId - The user's root Channel ID, which may be null or undefined.
 * @property {VigilantApiDtosUsersSelfGetResponse | undefined} userSelf - The user's full VigilantApiDtosUsersSelfGetResponse object.
 * @property {number | undefined} rootOrgOption - The selected root Channel option.
 */
export interface AppState {
  apiVersion: string | undefined;
  breadcrumbs: string[];
  dialogVisible: boolean;
  dialogModal: boolean;
  dialogTitle: string | undefined;
  dialogMessage: string | undefined;
  overlayVisible: boolean;
  spinnerVisible: boolean;
  toasts: ToastMessage[];
  userLoggedIn: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  userAuthData: any;
  userSuperAdmin: boolean | undefined;
  userId: number | undefined;
  userEmail: string | undefined;
  userFirstName: string | undefined;
  userLastName: string | undefined;
  userRootOrgId: number | null | undefined;
  userSelf: VigilantApiDtosUsersSelfGetResponse | undefined;
  rootOrgOption: number | undefined;
}

/**
 * The default state for the application.
 *
 * @type {AppState}
 */
const defaultState: AppState = {
  apiVersion: undefined,
  breadcrumbs: [],
  dialogVisible: false,
  dialogModal: false,
  dialogTitle: undefined,
  dialogMessage: undefined,
  overlayVisible: false,
  spinnerVisible: false,
  toasts: [],
  userLoggedIn: false,
  userAuthData: undefined,
  userSuperAdmin: undefined,
  userId: undefined,
  userEmail: undefined,
  userFirstName: undefined,
  userLastName: undefined,
  userRootOrgId: undefined,
  userSelf: undefined,
  rootOrgOption: undefined,
};

export const appReducer = createReducer(
  defaultState,

  /**
   * Handles setting the authentication state.
   *
   * Updates the user's logged-in status and authentication data.
   *
   * @param {AppState} state - The current application state.
   * @param {Object} action - The action containing userLoggedIn and userAuthData.
   * @returns {AppState} The new state with updated authentication details.
   */
  on(AppActions.SetAuthState, (state, action): AppState => {
    return {
      ...state,
      userLoggedIn: action.userLoggedIn,
      userAuthData: action.userAuthData,
    };
  }),

  /**
   * Handles setting the user information.
   *
   * Updates user-related fields such as admin status, ID, email, and names.
   *
   * @param {AppState} state - The current application state.
   * @param {Object} action - The action containing user info details.
   * @returns {AppState} The new state with updated user information.
   */
  on(AppActions.SetUserInfo, (state, action): AppState => {
    return {
      ...state,
      userSuperAdmin: action.userSuperAdmin,
      userId: action.userId,
      userEmail: action.userEmail,
      userFirstName: action.userFirstName,
      userLastName: action.userLastName,
      userRootOrgId: action.userRootOrgId,
      userSelf: action.userSelf,
    };
  }),

  /**
   * Handles setting the API version.
   *
   * Updates the apiVersion property in the state.
   *
   * @param {AppState} state - The current application state.
   * @param {Object} action - The action containing the apiVersion.
   * @returns {AppState} The new state with the updated API version.
   */
  on(AppActions.SetAPIVersion, (state, action): AppState => {
    return {
      ...state,
      apiVersion: action.apiVersion,
    };
  }),

  /**
   * Handles opening a dialog.
   *
   * Sets dialogVisible to true and updates dialog modal status, title, and message.
   *
   * @param {AppState} state - The current application state.
   * @param {Object} action - The action containing modal flag, title, and message.
   * @returns {AppState} The new state with the dialog opened.
   */
  on(AppActions.OpenDialog, (state, action): AppState => {
    return {
      ...state,
      dialogVisible: true,
      dialogModal: action.modal,
      dialogTitle: action.title,
      dialogMessage: action.message,
    };
  }),

  /**
   * Handles closing the dialog.
   *
   * Resets the dialog properties to hide it.
   *
   * @param {AppState} state - The current application state.
   * @returns {AppState} The new state with the dialog closed.
   */
  on(AppActions.CloseDialog, (state): AppState => {
    return {
      ...state,
      dialogVisible: false,
      dialogModal: false,
      dialogTitle: undefined,
      dialogMessage: undefined,
    };
  }),

  /**
   * Handles adding toast notifications.
   *
   * Appends new toast messages to the current toasts array.
   *
   * @param {AppState} state - The current application state.
   * @param {Object} action - The action containing an array of toast messages.
   * @returns {AppState} The new state with updated toasts.
   */
  on(AppActions.AddToasts, (state, action): AppState => {
    return {
      ...state,
      toasts: [...state.toasts, ...action.toasts],
    };
  }),

  /**
   * Handles clearing all toast notifications.
   *
   * Resets the toasts array to empty.
   *
   * @param {AppState} state - The current application state.
   * @returns {AppState} The new state with toasts cleared.
   */
  on(AppActions.ClearToasts, (state): AppState => {
    return {
      ...state,
      toasts: [],
    };
  }),

  /**
   * Handles showing the overlay.
   *
   * Sets overlayVisible to true.
   *
   * @param {AppState} state - The current application state.
   * @returns {AppState} The new state with the overlay shown.
   */
  on(AppActions.ShowOverlay, (state): AppState => {
    return {
      ...state,
      overlayVisible: true,
    };
  }),

  /**
   * Handles hiding the overlay.
   *
   * Sets overlayVisible to false.
   *
   * @param {AppState} state - The current application state.
   * @returns {AppState} The new state with the overlay hidden.
   */
  on(AppActions.HideOverlay, (state): AppState => {
    return {
      ...state,
      overlayVisible: false,
    };
  }),

  /**
   * Handles showing the spinner.
   *
   * Sets spinnerVisible to true.
   *
   * @param {AppState} state - The current application state.
   * @returns {AppState} The new state with the spinner shown.
   */
  on(AppActions.ShowSpinner, (state): AppState => {
    return {
      ...state,
      spinnerVisible: true,
    };
  }),

  /**
   * Handles hiding the spinner.
   *
   * Sets spinnerVisible to false.
   *
   * @param {AppState} state - The current application state.
   * @returns {AppState} The new state with the spinner hidden.
   */
  on(AppActions.HideSpinner, (state): AppState => {
    return {
      ...state,
      spinnerVisible: false,
    };
  }),

  /**
   * Handles showing block UI elements.
   *
   * Sets overlayVisible to true and updates spinnerVisible based on the action payload.
   *
   * @param {AppState} state - The current application state.
   * @param {Object} action - The action containing an optional spinner flag.
   * @returns {AppState} The new state with block UI elements shown.
   */
  on(AppActions.ShowBlockUI, (state, action): AppState => {
    return {
      ...state,
      overlayVisible: true,
      spinnerVisible: typeof action.spinner !== "undefined" ? action.spinner : state.spinnerVisible,
    };
  }),

  /**
   * Handles hiding block UI elements.
   *
   * Sets overlayVisible to false and updates spinnerVisible based on the action payload.
   *
   * @param {AppState} state - The current application state.
   * @param {Object} action - The action containing an optional spinner flag.
   * @returns {AppState} The new state with block UI elements hidden.
   */
  on(AppActions.HideBlockUI, (state, action): AppState => {
    return {
      ...state,
      overlayVisible: false,
      spinnerVisible: typeof action.spinner !== "undefined" ? action.spinner : state.spinnerVisible,
    };
  }),

  /**
   * Handles pushing a breadcrumb.
   *
   * Appends a new breadcrumb to the breadcrumbs array.
   *
   * @param {AppState} state - The current application state.
   * @param {Object} action - The action containing the breadcrumb to push.
   * @returns {AppState} The new state with the updated breadcrumbs.
   */
  on(AppActions.PushBreadcrumb, (state, action): AppState => {
    return {
      ...state,
      breadcrumbs: [...state.breadcrumbs, action.breadcrumb],
    };
  }),

  /**
   * Handles popping a breadcrumb.
   *
   * Removes the last breadcrumb from the breadcrumbs array.
   *
   * @param {AppState} state - The current application state.
   * @returns {AppState} The new state with the last breadcrumb removed.
   */
  on(AppActions.PopBreadcrumb, (state): AppState => {
    return {
      ...state,
      breadcrumbs: state.breadcrumbs.slice(0, state.breadcrumbs.length - 1),
    };
  }),

  /**
   * Handles setting the root Channel option.
   *
   * Updates the rootOrgOption property in the state.
   *
   * @param {AppState} state - The current application state.
   * @param {Object} action - The action containing the root Channel option.
   * @returns {AppState} The new state with the updated root Channel option.
   */
  on(AppActions.SetRootOrgOption, (state, action): AppState => {
    return {
      ...state,
      rootOrgOption: action.rootOrgOption,
    };
  })
);
